import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { chatKeys } from '../../keys'
import chatService from '../services'

import type { components } from '@/schema'

type SendChatMessageDto = components['schemas']['SendChatMessageDto']
type SendMultipleMessagesDto = components['schemas']['SendMultipleMessagesDto']
type ChatMessageListResponse = components['schemas']['ChatMessageListResponse']

export function useMutationSendMessage(roomId: string) {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: SendChatMessageDto) =>
			chatService.sendMessage(roomId, data),
		onSuccess: (response) => {
			// Optimistically add message to cache for immediate feedback
			if (response.data) {
				queryClient.setQueryData<ChatMessageListResponse>(
					chatKeys.messages(roomId),
					(old) => {
						if (!old) return old
						const exists = old.items.some((m) => m.id === response.data?.id)
						if (exists) return old
						return {
							...old,
							items: [...old.items, response.data!],
						}
					},
				)
			}
			// SSE chat:new_message handles rooms() invalidation
		},
		onError: () => {
			toast.error('ส่งข้อความไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}

export function useMutationSendMultipleMessages(roomId: string) {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: SendMultipleMessagesDto) =>
			chatService.sendMultipleMessages(roomId, data),
		onSuccess: (response) => {
			// Add all messages to cache for immediate feedback
			if (response.data && response.data.length > 0) {
				queryClient.setQueryData<ChatMessageListResponse>(
					chatKeys.messages(roomId),
					(old) => {
						if (!old) return old
						const existingIds = new Set(old.items.map((m) => m.id))
						const newMessages = response.data.filter(
							(m) => !existingIds.has(m.id),
						)
						if (newMessages.length === 0) return old
						return {
							...old,
							items: [...old.items, ...newMessages],
						}
					},
				)
			}
			// SSE chat:new_message handles rooms() invalidation
		},
		onError: () => {
			toast.error('ส่งข้อความไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
		},
	})
}
